import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../../../shared/services/project.service';
import { UserService } from '../../../shared/services/user.service';
import { TenantService } from '../../../shared/services/tenant.service';
import { User } from '../../../shared/services/user';
import { Project, ProjectStage, ProjectStatus } from '../../../shared/models/project.model';
import { TaskService } from '../../../shared/services/task.service';
import { User as UserModel, Tenant } from '../../../shared/models/user.model';
import { Task } from '../../../shared/models/task.model';
import { ListProjectsComponent } from './list-projects/list-projects';
import { AddStageProjectComponent } from './add-stage-project/add-stage-project';

// DISPLAY ALL PROJECT IN LIST
@Component({
  selector: 'app-display-projects',
  imports: [CommonModule, FormsModule, ListProjectsComponent, AddStageProjectComponent],
  templateUrl: './display-projects.html'
})
export class DisplayProjectsComponent implements OnInit {
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private tenantService = inject(TenantService);
  user = inject(User); // User service from user.ts for Cognito admin check
  private taskService = inject(TaskService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @Output() openAdd = new EventEmitter<void>();

  showAddStageModal = false;
  selectedProjectIdForNewStage: string | null = null;
  selectedProjectStartDate: string | null = null;

  private allProjects: Project[] = [];
  private accessibleProjects: Project[] = []; // access-filtered, not yet search/status-filtered
  projects: Project[] = [];                    // final list shown in the UI
  projectStages: ProjectStage[] = [];
  employees: UserModel[] = [];
  tasks: Task[] = [];
  currentUserId: string | null = null;

  // Search state
  searchQuery = '';

  // Scope tab filter: all projects vs only those assigned to me.
  // Admins can toggle; non-admins only ever see their assigned projects.
  scopeFilter: 'all' | 'mine' = 'all';

  /**
   * Lifecycle filter. '' is the default and means "everything except archived":
   * archived projects are meant to be out of the way, reachable by picking
   * Archived explicitly.
   */
  statusFilter: '' | ProjectStatus = '';

  private isDone(project: Project): boolean {
    if (project.status === 'DONE' || project.status === 'ARCHIVED') return true;
    if (project.status === 'STANDBY') return false;
    const stages = this.projectStages.filter(s => s.projectId === project.projectId);
    return stages.length > 0 && stages.every(s => s.status === 'DONE');
  }

  /** A project with no stored status behaves as ACTIVE. */
  private statusOf(project: Project): ProjectStatus {
    return project.status ?? 'ACTIVE';
  }

  onStatusFilterChange(): void {
    this.applyFilters();
  }

  /** True when the current user is a manager, member, or task assignee on the project. */
  private isAssignedToMe(project: Project): boolean {
    const uid = this.currentUserId;
    if (!uid) return false;
    if (project.managers?.some(m => m.userId === uid)) return true;
    if (project.members?.some(m => m.userId === uid)) return true;
    const stageIds = this.projectStages
      .filter(s => s.projectId === project.projectId)
      .map(s => s.projectStageId);
    return this.tasks.some(t =>
      stageIds.includes(t.projectStageId) && Array.isArray(t.employeeId) && t.employeeId.includes(uid),
    );
  }

  /**
   * The un-searched project set for the current scope. Filtered in-memory from the
   * already-loaded projects (admins have all; non-admins have their accessible set),
   * so switching scope is instant — no extra network round-trips.
   */
  private scopeSource(): Project[] {
    let s = this.accessibleProjects;
    if (this.user.admin() && this.scopeFilter === 'mine') {
      s = s.filter(p => this.isAssignedToMe(p));
    }
    return s;
  }

  /** Counts describe the visible scope, so archived projects stay out of them. */
  private countable(): Project[] {
    return this.scopeSource().filter(p => this.statusOf(p) !== 'ARCHIVED');
  }

  get activeProjectsCount(): number {
    return this.countable().filter(p => !this.isDone(p)).length;
  }

  get doneProjectsCount(): number {
    return this.countable().filter(p => this.isDone(p)).length;
  }

  get archivedProjectsCount(): number {
    return this.scopeSource().filter(p => this.statusOf(p) === 'ARCHIVED').length;
  }

  onScopeTab(filter: 'all' | 'mine'): void {
    this.scopeFilter = filter;
    this.applyFilters();
  }

  // Tenant filter (admin or multi-tenant user)
  tenants: Tenant[] = [];
  selectedTenantId: string = '';
  showTenantFilter = false;
  private userTenantIds: string[] = [];
  isLoading = true;

  viewMode: 'list' = 'list';
  private defaultTenantApplied = false;

  ngOnInit() {
    // Global top-bar search deep-links here via ?q=…
    this.route.queryParams.subscribe(params => {
      const q = params['q'] ?? '';
      if (q !== this.searchQuery) {
        this.searchQuery = q;
        this.applyFilters();
      }
    });

    // Restore previous tenant selection if any was set in ProjectService to preserve filter on back/exit navigation
    if (this.projectService.activeTenantIds && this.projectService.activeTenantIds.length === 1) {
      this.selectedTenantId = this.projectService.activeTenantIds[0];
      this.defaultTenantApplied = true;
      this.showTenantFilter = true;
    }

    this.projectService.projects$.subscribe(projects => {
      this.allProjects = projects;
      this.isLoading = false;
      this.filterAccessibleProjects();
    });
    this.projectService.projectsStage$.subscribe(stages => {
      this.projectStages = stages;
      this.applyFilters(); // re-apply when stages load (status derivation depends on stages)
    });
    this.taskService.tasks$.subscribe(tasks => {
      this.tasks = tasks;
      this.filterAccessibleProjects();
    });
    this.userService.users$.subscribe(emps => { this.employees = emps; });
    this.userService.currentUser$.subscribe(user => {
      this.currentUserId = user?.id || null;
      // Apply server-side tenant filter: non-admins only see their tenant's projects
      if (this.user.admin()) {
        // Don't override if a default tenant is already applied
        if (!this.defaultTenantApplied) {
          this.projectService.setTenantFilter(null);
        }
        this.userTenantIds = [];
        // Admin filter visibility is driven by tenants$ — keep current value
        this.showTenantFilter = this.tenants.length > 0 || this.defaultTenantApplied;
      } else {
        const tenantIds = user?.tenants?.map(t => t.tenantId) ?? [];
        this.userTenantIds = tenantIds;
        // Don't override if a default tenant is already applied
        if (!this.defaultTenantApplied) {
          this.projectService.setTenantFilter(tenantIds.length > 0 ? tenantIds : null);
        }
        // Show tenant filter for any user with tenants
        if (tenantIds.length >= 1 && user?.tenants) {
          this.tenants = user.tenants
            .filter(ut => ut.tenant)
            .map(ut => ut.tenant as Tenant);
          this.showTenantFilter = true;
          // If user has exactly 1 tenant and no default set, auto-select it
          if (tenantIds.length === 1 && !this.defaultTenantApplied) {
            this.selectedTenantId = tenantIds[0];
          }
        } else {
          this.showTenantFilter = false;
        }
      }
      this.filterAccessibleProjects();
    });
    this.userService.listUsers();
    this.userService.settings$.subscribe(settings => {
      // Auto-select default tenant on first load — but NOT for admins, who should
      // see every project by default (they can still filter manually via the dropdown).
      if (!this.user.admin() && !this.defaultTenantApplied && settings?.defaultTenantId) {
        this.defaultTenantApplied = true;
        this.selectedTenantId = settings.defaultTenantId;
        this.showTenantFilter = true;
        this.projectService.setTenantFilter([settings.defaultTenantId]);
      }
    });

    // Load tenants for admin filter dropdown
    this.tenantService.tenants$.subscribe(tenants => {
      if (this.user.admin()) {
        this.tenants = tenants;
        this.showTenantFilter = tenants.length > 0;
      }
    });

    if (this.user.admin()) {
      this.tenantService.listTenants();
    }
  }

  // ── Search ──────────────────────────────────────────────────────────────────

  onSearchChange(): void {
    this.applyFilters();
  }

  onTenantFilterChange(): void {
    this.isLoading = true;
    if (this.selectedTenantId) {
      this.projectService.setTenantFilter([this.selectedTenantId]);
    } else if (this.user.admin()) {
      // Admin clearing filter: show all projects
      this.projectService.setTenantFilter(null);
    } else {
      // Non-admin clearing filter: reset to all their tenants
      this.projectService.setTenantFilter(this.userTenantIds.length > 0 ? this.userTenantIds : null);
    }
  }

  private applyFilters(): void {
    const query = this.searchQuery.trim().toLowerCase();
    let result = this.scopeSource();
    if (query) {
      result = result.filter(p => p.projectName.toLowerCase().includes(query));
    }
    result = this.statusFilter
      ? result.filter(p => this.statusOf(p) === this.statusFilter)
      : result.filter(p => this.statusOf(p) !== 'ARCHIVED');
    this.projects = [...result];
  }

  updateStage(stageId: string, changes: Partial<ProjectStage>) {
    this.projectService.updateProjectStage(stageId, changes)
      .then(() => console.log('Stage updated'))
      .catch(err => console.error(err));
  }

  updateProject(projectId: string, changes: any) {
    this.projectService.updateProject(projectId, changes)
      .then(() => console.log('Project updated'))
      .catch(err => console.error(err));
  }

  openAddStageModal(projectId: string) {
    this.selectedProjectIdForNewStage = projectId;

    // Find the project and get its start date
    const project = this.projects.find(p => p.projectId === projectId);
    this.selectedProjectStartDate = project?.startDate || null;

    this.showAddStageModal = true;
  }

  closeAddStageModal() {
    this.showAddStageModal = false;
    this.selectedProjectIdForNewStage = null;
    this.selectedProjectStartDate = null;
  }

  goToDetail(projectId: string) {
    this.router.navigate(['/projects', projectId]);
  }

  // ── Access Filtering ───────────────────────────────────────────────────────

  /** Computes which projects the current user can access, then applies search/status filters. */
  private filterAccessibleProjects(): void {
    if (this.user.admin()) {
      this.accessibleProjects = this.sortByName(this.allProjects);
      this.applyFilters();
      return;
    }

    if (!this.currentUserId) {
      this.accessibleProjects = [];
      this.projects = [];
      return;
    }

    this.accessibleProjects = this.sortByName(
      this.allProjects.filter(project => this.isAssignedToMe(project)),
    );
    this.applyFilters();
  }

  private sortByName(projects: Project[]): Project[] {
    return [...projects].sort((a, b) =>
      a.projectName.localeCompare(b.projectName, undefined, { numeric: true, sensitivity: 'base' })
    );
  }
}
